
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Loader2 } from 'lucide-react';
import { CourierSelector } from './CourierSelector';
import { PaymentMethodSelector } from './PaymentMethodSelector';
import { useCollectionForm } from '../context/CollectionFormContext';
import { toast } from '@/components/ui/sonner';
import { CollectionStatusDisplay } from './CollectionStatusDisplay';

interface SimpleCollectionFormProps {
  onSubmit: (data: any) => Promise<boolean>;
  onCancel: () => void;
}

export const SimpleCollectionForm = ({ onSubmit, onCancel }: SimpleCollectionFormProps) => {
  const {
    loading,
    submitting,
    selectedCourierId,
    paymentMethods,
    filteredCouriers,
    transactionStatus,
    setTransactionStatus,
    transactionId,
    setTransactionId,
    statusMessage,
    setStatusMessage,
    formData,
    setFormData
  } = useCollectionForm();

  const form = useForm({
    defaultValues: {
      courierId: '',
      paymentMethodId: '',
      amount: 0,
      reason: '',
      isAuthRequest: false
    }
  });

  const isAuthRequest = form.watch('isAuthRequest');
  const amount = form.watch('amount');
  const reason = form.watch('reason');
  const paymentMethodId = form.watch('paymentMethodId');

  // Обновляем ID курьера в форме при изменении выбора
  useEffect(() => {
    if (selectedCourierId) {
      console.log('Установка courierId в форме:', selectedCourierId);
      form.setValue('courierId', selectedCourierId);
    } else {
      form.setValue('courierId', '');
      form.setValue('paymentMethodId', '');
    }
  }, [selectedCourierId, form]);

  // Текущий выбранный курьер
  const selectedCourier = filteredCouriers.find(c => c.id === selectedCourierId);
  const courierName = selectedCourier?.name || 'Неизвестный курьер';

  const handleFormSubmit = async (data: any) => {
    if (!data.courierId) {
      toast.error('Необходимо выбрать курьера');
      return;
    }
    
    // Проверяем наличие платежного метода для прямого списания
    if (!isAuthRequest && paymentMethods.length > 0 && !data.paymentMethodId) {
      toast.error('Необходимо выбрать платежный метод для списания');
      return;
    }
    
    console.log('Подготовка к отправке формы взыскания:', data);
    console.log('Выбранный курьер:', selectedCourierId, courierName);
    console.log('Доступные курьеры:', filteredCouriers.length);
    
    // Сохраняем данные формы для отображения в статусе
    setFormData({
      amount: data.amount,
      reason: data.reason,
      isAuthRequest: data.isAuthRequest,
      courierName: courierName
    });

    // Устанавливаем начальный статус загрузки
    setTransactionStatus('loading');
    
    try {
      // Запускаем обработку формы
      console.log('Отправляем форму...');
      const success = await onSubmit(data);
      console.log('Результат отправки формы:', success);
      
      // Форму сбрасываем только если пользователь нажал "Новое взыскание" или "Закрыть"
      if (!success) {
        // Если ошибка, устанавливаем статус ошибки
        setTransactionStatus('error');
      }
    } catch (error) {
      console.error('Ошибка при отправке формы:', error);
      setTransactionStatus('error');
      setStatusMessage(`Ошибка: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
    }
  };

  const handleReset = () => {
    // Сбросить статус и вернуться к форме
    setTransactionStatus(null);
    setTransactionId(null);
    setStatusMessage(null);
  };

  // Проверяем, нужно ли показать форму или статус
  const hasPaymentMethods = paymentMethods.length > 0;
  
  // Если есть активный статус транзакции, показываем компонент статуса
  if (transactionStatus) {
    console.log('Показываем статус транзакции:', transactionStatus);
    return (
      <CollectionStatusDisplay
        status={transactionStatus}
        amount={formData.amount}
        reason={formData.reason}
        courierName={formData.courierName}
        isAuthRequest={formData.isAuthRequest}
        transactionId={transactionId || undefined}
        message={statusMessage || undefined}
        onReset={handleReset}
        onClose={onCancel}
      />
    );
  }
  
  // Иначе показываем форму
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="courierId"
          render={() => (
            <FormItem>
              <FormLabel>Курьер</FormLabel>
              <FormControl>
                <CourierSelector />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {selectedCourierId && hasPaymentMethods && (
          <PaymentMethodSelector form={form} isAuthRequest={isAuthRequest} />
        )}
        
        <FormField
          control={form.control}
          name="amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Сумма</FormLabel>
              <FormControl>
                <Input 
                  type="number" 
                  placeholder="Введите сумму" 
                  min={0}
                  {...field}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="reason"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {isAuthRequest ? 'Комментарий к авторизации' : 'Причина взыскания'}
              </FormLabel>
              <FormControl>
                <Textarea 
                  placeholder={isAuthRequest ? 
                    "Добавьте комментарий к запросу авторизации" : 
                    "Опишите причину взыскания"
                  } 
                  className="resize-none min-h-[80px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="isAuthRequest"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel>Запрос на авторизацию карты</FormLabel>
                <p className="text-sm text-muted-foreground">
                  Выберите, если это запрос на авторизацию платежной карты без фактического списания средств
                </p>
              </div>
            </FormItem>
          )}
        />
        
        <DialogFooter>
          <Button 
            type="button" 
            variant="outline" 
            onClick={onCancel}
            disabled={submitting}
          >
            Отмена
          </Button>
          <Button 
            type="submit" 
            disabled={submitting || !selectedCourierId || amount <= 0 || (!isAuthRequest && hasPaymentMethods && !paymentMethodId)}
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isAuthRequest ? 'Запросить авторизацию' : 'Создать взыскание'}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};
